import type {
  Cadence,
  ReportCustomerDto,
  ReportMeasurementPeriodOptionDto,
  ReportReportingMonthOptionDto,
  ReportTemplateDto,
} from '@act/shared/data-transfer-objects';
import { MenuItem, Select, TextField, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Button, FlexContainer, LinkButton } from '@act/storybook';
import ReportFormField from './ReportFormField';
import ReportMetadataReportingSection from './ReportMetadataReportingSection';
import ReportsSurface from './ReportsSurface';

import type { MeasurementPeriodMatchStatus } from '../utils/reportMetadataFormOptions.util';

/** Form mode for report metadata fields. */
export type ReportMetadataFormMode = 'create' | 'edit';

interface ReportMetadataFormFieldsProps {
  mode: ReportMetadataFormMode;
  name: string;
  nameTouched: boolean;
  customerId: string;
  cadence: Cadence;
  cadenceLabel: string;
  measurementPeriod: string;
  endMonthKey: string;
  templateId: string;
  customers: ReportCustomerDto[];
  templates: ReportTemplateDto[];
  measurementPeriodOptions: ReportMeasurementPeriodOptionDto[];
  reportingMonthOptions: ReportReportingMonthOptionDto[];
  measurementPeriodsLoading: boolean;
  reportingMonthsLoading: boolean;
  measurementPeriodMatchStatus: MeasurementPeriodMatchStatus;
  autoMatchedMeasurementPeriodLabel: string;
  defaultReportName: string;
  submitting: boolean;
  canSubmit: boolean;
  onNameChange: (value: string) => void;
  onNameFocus: () => void;
  onResetNameToDefault: () => void;
  onCustomerChange: (customerId: string) => void;
  onCadenceChange: (cadence: Cadence) => void;
  onMeasurementPeriodChange: (value: string) => void;
  onEndMonthKeyChange: (value: string) => void;
  onTemplateChange: (templateId: string) => void;
  onSubmit: () => void;
  submitLabel: string;
  cancelTo?: string;
}

/**
 * Shared metadata fields for report create and edit screens.
 * @param props - Form state, options, and change handlers.
 * @returns Grouped report metadata form sections.
 */
const ReportMetadataFormFields = ({
  mode,
  name,
  nameTouched,
  customerId,
  cadence,
  cadenceLabel,
  measurementPeriod,
  endMonthKey,
  templateId,
  customers,
  templates,
  measurementPeriodOptions,
  reportingMonthOptions,
  measurementPeriodsLoading,
  reportingMonthsLoading,
  measurementPeriodMatchStatus,
  autoMatchedMeasurementPeriodLabel,
  defaultReportName,
  submitting,
  canSubmit,
  onNameChange,
  onNameFocus,
  onResetNameToDefault,
  onCustomerChange,
  onCadenceChange,
  onMeasurementPeriodChange,
  onEndMonthKeyChange,
  onTemplateChange,
  onSubmit,
  submitLabel,
  cancelTo,
}: ReportMetadataFormFieldsProps) => {
  const selectedCustomer = useMemo(
    () => customers.find(customer => customer.id === customerId),
    [customers, customerId],
  );

  const showResetName =
    nameTouched && name.trim() !== defaultReportName.trim();

  return (
    <FlexContainer
      direction="column"
      gap={2}
      width="100%"
      maxWidth="720px"
      height="auto"
      overflowAllVisible={true}
    >
      <ReportsSurface>
        <FlexContainer
          direction="column"
          gap={2}
          width="100%"
          height="auto"
          overflowAllVisible={true}
        >
          <Typography variant="subtitle1" component="h2">
            Report details
          </Typography>

          <ReportFormField label="Customer">
            {mode === 'edit' ? (
              <Typography variant="body1">
                {selectedCustomer?.name ?? customerId}
              </Typography>
            ) : (
              <Select
                required
                fullWidth
                value={customerId}
                onChange={event => onCustomerChange(event.target.value)}
              >
                <MenuItem value="" disabled>
                  Select customer
                </MenuItem>
                {customers.map(customer => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </ReportFormField>

          <ReportFormField label="Report name">
            <TextField
              required
              fullWidth
              value={name}
              onChange={event => onNameChange(event.target.value)}
              onFocus={onNameFocus}
              helperText="Defaults to Customer: Measurement period until you change it."
            />
            {showResetName ? (
              <Button
                variant="text"
                size="small"
                trackingName="reports-metadata-reset-name"
                onClick={onResetNameToDefault}
              >
                Reset to default
              </Button>
            ) : null}
          </ReportFormField>
        </FlexContainer>
      </ReportsSurface>

      <ReportMetadataReportingSection
        mode={mode}
        customerId={customerId}
        cadence={cadence}
        cadenceLabel={cadenceLabel}
        measurementPeriod={measurementPeriod}
        endMonthKey={endMonthKey}
        templateId={templateId}
        templates={templates}
        measurementPeriodOptions={measurementPeriodOptions}
        reportingMonthOptions={reportingMonthOptions}
        measurementPeriodsLoading={measurementPeriodsLoading}
        reportingMonthsLoading={reportingMonthsLoading}
        measurementPeriodMatchStatus={measurementPeriodMatchStatus}
        autoMatchedMeasurementPeriodLabel={autoMatchedMeasurementPeriodLabel}
        onCadenceChange={onCadenceChange}
        onMeasurementPeriodChange={onMeasurementPeriodChange}
        onEndMonthKeyChange={onEndMonthKeyChange}
        onTemplateChange={onTemplateChange}
      />

      <FlexContainer
        direction="row"
        gap={1.5}
        alignItems="center"
        height="auto"
        overflowAllVisible={true}
      >
        <Button
          type="button"
          variant="contained"
          disabled={!canSubmit || submitting}
          trackingName={
            mode === 'create'
              ? 'reports-create-submit'
              : 'reports-edit-submit'
          }
          onClick={onSubmit}
        >
          {submitting ? 'Saving…' : submitLabel}
        </Button>
        {cancelTo ? (
          <LinkButton
            to={{ pathname: cancelTo }}
            trackingName="reports-edit-cancel"
          >
            Cancel
          </LinkButton>
        ) : null}
      </FlexContainer>
    </FlexContainer>
  );
};

export default ReportMetadataFormFields;
